import { useState, type ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight, Check, Info } from "lucide-react";
import {
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState as UiEmptyState,
  Icon,
  Input,
  SegmentedControl,
  Skeleton,
  Tooltip,
  cn,
  type SegmentedOption,
} from "@winche/ui";
import { cloudLoggingUrl, failureTitle, type AdminFailure } from "../lib/callable-error";
import { relativeTime } from "../lib/format";
import { useNow } from "../lib/hooks";

/*
 * Admin building blocks on top of @winche/ui (audit 2026-10-10, admin-02/07/08):
 * the app's tokens, Inter and primitives, with one short type scale
 * (admin-M2) — page title 20/28 semibold, block title 14 medium, KPI value
 * 24 semibold tabular, body and tables 13 (`text-label`), metadata 12
 * (`text-caption`). Nothing under 12 px except the nav overline.
 */

export type StatTone = "default" | "good" | "warn" | "bad";

const TONE_CLASS: Record<StatTone, string> = {
  default: "text-foreground",
  good: "text-success",
  warn: "text-warning-fg",
  bad: "text-danger-fg",
};

/** (i) next to a label: the definition on hover or focus, two lines at most (admin-14). */
export function InfoHint({ text, className }: { text: string; className?: string }) {
  return (
    <Tooltip content={text} className={className}>
      <button
        type="button"
        aria-label={text}
        className="relative inline-grid size-4 place-items-center rounded-full text-subtle-foreground transition-colors duration-150 after:absolute after:-inset-2 hover:text-foreground"
      >
        <Icon icon={Info} size="xs" />
      </button>
    </Tooltip>
  );
}

/** Change against the previous period. `goodWhen` says which direction is healthy. */
export interface MetricDelta {
  value: number | null;
  /** Rendered text ("+12%"); defaults to the signed percent of `value`. */
  label?: string;
  goodWhen?: "up" | "down";
  /** Muted caption after the delta ("vs last run"). */
  caption?: string;
}

/**
 * KPI tile (admin-07): label (+ optional definition), value, delta and a
 * secondary line. Color only for real thresholds (`tone`): a value above zero
 * is not "good" by itself.
 */
export function Metric({
  label,
  value,
  sub,
  tone = "default",
  hint,
  delta,
  size = "md",
  className,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  tone?: StatTone;
  hint?: string;
  delta?: MetricDelta;
  /** lg: the hero numbers of a section. */
  size?: "md" | "lg";
  className?: string;
}) {
  return (
    <div className={cn("min-w-0 rounded-lg border border-border bg-card p-4", className)}>
      <div className="mb-1.5 flex items-center gap-1.5">
        <p className="truncate text-caption text-muted-foreground">{label}</p>
        {hint ? <InfoHint text={hint} /> : null}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <p
          className={cn(
            "font-semibold leading-none tabular-nums",
            size === "lg" ? "text-3xl tracking-tight" : "text-2xl",
            TONE_CLASS[tone],
          )}
        >
          {value}
        </p>
        {delta && delta.value != null ? <DeltaChip delta={delta} /> : null}
      </div>
      {sub ? <p className="mt-1.5 text-caption text-subtle-foreground">{sub}</p> : null}
    </div>
  );
}

function DeltaChip({ delta }: { delta: MetricDelta }) {
  const v = delta.value ?? 0;
  // No change is neither good nor bad: muted, without an arrow (a red ↗0 read as a regression).
  const flat = v === 0;
  const up = v > 0;
  const good = (delta.goodWhen ?? "up") === "up" ? up : !up;
  const text = delta.label ?? (flat ? "0%" : `${up ? "+" : "−"}${Math.abs(v * 100).toFixed(0)}%`);
  return (
    <span className="inline-flex items-center gap-1 text-caption">
      <span
        className={cn(
          "inline-flex items-center font-medium tabular-nums",
          flat ? "text-muted-foreground" : good ? "text-success" : "text-danger-fg",
        )}
      >
        {flat ? null : <Icon icon={up ? ArrowUpRight : ArrowDownRight} size="xs" />}
        {text}
      </span>
      {delta.caption ? <span className="text-subtle-foreground">{delta.caption}</span> : null}
    </span>
  );
}

const GRID_COLS = {
  2: "grid-cols-2",
  3: "grid-cols-1 @md:grid-cols-3",
  4: "grid-cols-2 @3xl:grid-cols-4",
  5: "grid-cols-2 @2xl:grid-cols-3 @5xl:grid-cols-5",
  6: "grid-cols-2 @2xl:grid-cols-3 @5xl:grid-cols-6",
} as const;

/**
 * Metric grid sized by the content area, not the window (admin-11): `<main>`
 * is a container, so a 1024 px window with the sidebar still gets 2–3
 * columns instead of six 70 px tiles.
 */
export function MetricGrid({ cols = 4, children, className }: { cols?: keyof typeof GRID_COLS; children: ReactNode; className?: string }) {
  return <div className={cn("grid gap-3", GRID_COLS[cols], className)}>{children}</div>;
}

/** Bordered card surface for a block (charts, lists, forms). */
export function Panel({ children, className, padded = true }: { children: ReactNode; className?: string; padded?: boolean }) {
  return <div className={cn("rounded-lg border border-border bg-card", padded && "p-4", className)}>{children}</div>;
}

/** A block: title (14 medium, sentence case), optional definition and action, then its content. */
export function Block({
  title,
  hint,
  action,
  children,
  className,
  id,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section className={cn("mb-8", className)} aria-labelledby={id}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <h2 id={id} className="text-sm font-medium text-foreground">
            {title}
          </h2>
          {hint ? <InfoHint text={hint} /> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** One line under a block with the data source ("Source: _admin/aiDailySeries · hourly"). */
export function SourceNote({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn("mt-2 text-caption text-subtle-foreground", className)}>{children}</p>;
}

/**
 * Data freshness instead of an always-pulsing "Live" (admin-08): a steady dot
 * with "Updated 2m ago" — green when recent, amber when older than
 * `staleAfterMs`, red when the listener failed — and the absolute UTC time and
 * the source in the tooltip.
 */
export function Freshness({
  at,
  failed = false,
  source,
  staleAfterMs = 60 * 60_000,
}: {
  at: number | null | undefined;
  failed?: boolean;
  source: string;
  staleAfterMs?: number;
}) {
  const now = useNow();
  const rel = relativeTime(at ?? null, now);
  const state = failed ? "failed" : at == null ? "unknown" : now - at > staleAfterMs ? "stale" : "fresh";
  const dot = {
    failed: "bg-danger",
    unknown: "bg-subtle-foreground",
    stale: "bg-warning",
    fresh: "bg-success",
  }[state];
  const text = failed ? "Listener failed" : at == null ? "Not updated yet" : `Updated ${rel.label}`;
  const detail = [rel.title, source].filter(Boolean).join(" · ");
  return (
    <Tooltip content={detail || source}>
      <span
        tabIndex={0}
        className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-caption text-muted-foreground"
      >
        <span className={cn("size-1.5 rounded-full", dot)} aria-hidden="true" />
        {text}
      </span>
    </Tooltip>
  );
}

/** Page title for a section, with its freshness or status chip and the main action. */
export function SectionHeader({
  title,
  subtitle,
  meta,
  action,
}: {
  title: string;
  subtitle?: string;
  /** Freshness indicator or a status chip next to the title. */
  meta?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {meta}
        </div>
        {subtitle && <p className="mt-1 text-label text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ message }: { message: string }) {
  return <UiEmptyState title={message} variant="dashed" size="compact" />;
}

/**
 * A failure that says what failed, its code and when, with Retry and a link
 * to the function's logs (satellite-v02). A plain `message` still works for
 * errors that are not calls.
 */
export function ErrorMessage({
  message,
  failure,
  onRetry,
  retrying,
}: {
  message?: string;
  failure?: AdminFailure | null;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined;
  const logs = failure?.fn && projectId ? cloudLoggingUrl(failure.fn, projectId, failure.at) : null;
  const action =
    onRetry || logs ? (
      <div className="flex flex-wrap items-center gap-2">
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} loading={retrying}>
            Retry
          </Button>
        )}
        {logs && (
          <a className="text-label underline underline-offset-2" href={logs} target="_blank" rel="noopener noreferrer">
            View logs
          </a>
        )}
      </div>
    ) : undefined;
  return (
    <Callout tone="danger" className="mb-4" live="assertive" title={failure ? failureTitle(failure) : undefined} action={action}>
      {failure ? failure.detail : message}
    </Callout>
  );
}

export function LoadButton({ loading, loaded, onLoad }: { loading: boolean; loaded: boolean; onLoad: () => void }) {
  return (
    <Button variant="outline" size="sm" onClick={onLoad} loading={loading} className="min-w-20 shrink-0">
      {loaded ? "Refresh" : "Load"}
    </Button>
  );
}

/**
 * Loading placeholder with the final geometry (admin-08): a row of metric
 * tiles and table rows, never an empty page with a busy button.
 */
export function LoadingBlock({ metrics = 4, rows = 6, label = "Loading" }: { metrics?: number; rows?: number; label?: string }) {
  return (
    <div role="status" aria-label={label} className="mb-8">
      {metrics > 0 ? (
        <MetricGrid cols={metrics >= 5 ? 5 : 4} className="mb-6">
          {Array.from({ length: metrics }, (_, i) => (
            <div key={i} className="rounded-lg border border-border bg-card p-4">
              <Skeleton className="mb-3 h-3 w-24" />
              <Skeleton className="h-6 w-20" />
            </div>
          ))}
        </MetricGrid>
      ) : null}
      {rows > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="flex h-9 items-center gap-4 border-b border-border px-3 last:border-b-0">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="ml-auto h-3 w-16" />
              <Skeleton className="h-3 w-12" />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Segmented selector with the admin's size (32 px with a mouse, 44 with a finger). */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  label: string;
  className?: string;
}) {
  return <SegmentedControl value={value} onValueChange={onChange} options={options} aria-label={label} size="sm" className={className} />;
}

/** Check + "Saved" next to a button; drive it with `useFlash` (lib/hooks). */
export function SavedCheck({ visible, children = "Saved" }: { visible: boolean; children?: ReactNode }) {
  return (
    <span
      role="status"
      className={cn(
        "inline-flex items-center gap-1 text-caption text-success transition-opacity duration-200 motion-reduce:transition-none",
        visible ? "opacity-100" : "opacity-0",
      )}
    >
      {visible ? (
        <>
          <Icon icon={Check} size="xs" />
          {children}
        </>
      ) : null}
    </span>
  );
}

/** 8 px status dot. */
export function ToneDot({ tone, className }: { tone: "ok" | "warn" | "bad" | "idle"; className?: string }) {
  const color = { ok: "bg-success", warn: "bg-warning", bad: "bg-danger", idle: "bg-subtle-foreground" }[tone];
  return <span aria-hidden="true" className={cn("inline-block size-2 shrink-0 rounded-full", color, className)} />;
}

/**
 * Confirmation for anything that writes to production (admin-01, spec 25 §5.3):
 * says the scope, and for the riskiest switches asks to type a word.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  scope,
  confirmLabel,
  destructive = true,
  typeToConfirm,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** What changes and for whom ("Every user gets PRO limits"). */
  scope: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  /** Exact word the operator must type (e.g. "TEST"). */
  typeToConfirm?: string;
  onConfirm: () => Promise<void> | void;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = !typeToConfirm || typed.trim() === typeToConfirm;

  const close = (next: boolean) => {
    if (busy) return;
    if (!next) {
      setTyped("");
      setError(null);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{scope}</DialogDescription>
        </DialogHeader>
        {typeToConfirm ? (
          <label className="mt-4 block text-label text-muted-foreground">
            Type <span className="font-mono font-semibold text-foreground">{typeToConfirm}</span> to confirm
            <Input
              className="mt-1.5"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
        ) : null}
        {error ? <p className="mt-3 text-label text-danger-fg">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => close(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            size="sm"
            disabled={!ready}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onConfirm();
                setTyped("");
                onOpenChange(false);
              } catch (err) {
                setError(err instanceof Error ? err.message : "Failed");
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
